import React, { useState, useRef, useEffect } from 'react';
import { AppIcon } from '../shared/icons';
import { useTranslation } from '../../i18n';
import './BurgerMenu.css';

interface MenuItem {
  id: string;
  label?: string;
  icon?: string;
  onClick?: () => void;
  href?: string;
  isSectionHeader?: boolean;
  isDivider?: boolean;
}

interface BurgerMenuProps {
  items: MenuItem[];
}

const BurgerMenu: React.FC<BurgerMenuProps> = ({ items }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleItemClick = (item: MenuItem) => {
    if (item.href) {
      window.open(item.href, '_blank', 'noopener,noreferrer');
      setIsOpen(false);
    } else if (item.onClick) {
      item.onClick();
      setIsOpen(false);
    }
  };

  return (
    <div ref={menuRef} className="burger-menu">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`burger-menu__toggle${isOpen ? ' burger-menu__toggle--open' : ''}`}
        aria-label={t('common.menu')}
        aria-expanded={isOpen}
      >
        <AppIcon name="menu" />
      </button>

      {isOpen && (
        <div className="burger-menu__dropdown">
          {items.map((item) => {
            if (item.isDivider) {
              return <div key={item.id} className="burger-menu__divider" />;
            }

            if (item.isSectionHeader) {
              return (
                <div key={item.id} className="burger-menu__section-header">
                  {item.label}
                </div>
              );
            }

            if (item.href) {
              return (
                <a
                  key={item.id}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="burger-menu__item"
                  onClick={() => setIsOpen(false)}
                >
                  <span className="burger-menu__item-icon"><AppIcon name="openInNew" fontSize="small" /></span>
                  <span>{item.label}</span>
                </a>
              );
            }

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleItemClick(item)}
                className="burger-menu__item"
              >
                {item.icon && <span className="burger-menu__item-icon">{item.icon}</span>}
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BurgerMenu;
